<script lang="ts" setup>
import projectStore from '@/store/ProjectStore.ts'
import userStore from '@/store/UserStore.ts'
import { Heatpump } from '@/services/models/implementations/Heatpump.ts'
import { CharacteristicType, ProductType } from '@/helper/Types.ts'
import { ECCharacteristic } from '@/services/models/ECCharacteristic.ts'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { hj } from '@/helper/analytics.ts'

const unfilteredHeatPumpList = ref<Heatpump[]>(projectStore.products[ProductType.HEATPUMP] as Heatpump[])

const activeVoltageFilter = ref<string>('')
const activeTechnologyFilter = ref<string[]>([])

if (!projectStore.projectProperties.heatPumpId.val) {
  if (userStore.preselectHeatPumpVoltageFilter230V && !isVoltageBtnDisabled('230V')) {
    activeVoltageFilter.value = '230V'
  }

  if (userStore.preselectHeatPumpVoltageFilter400V && !isVoltageBtnDisabled('400V')) {
    activeVoltageFilter.value = '400V'
  }
}

onMounted(() => filterHeatPumpList())
onUnmounted((): void => {
  projectStore.products[ProductType.HEATPUMP] = unfilteredHeatPumpList.value
})

type TechnologyFilter = { marketingName: string, technologyKey: string, disabled: boolean }

const technologyFilterOptions = computed<TechnologyFilter[]>(() => {
  const technologyFilter: Record<string, TechnologyFilter> = {}

  unfilteredHeatPumpList.value
    .filter(heatPump => heatPump.marketingName)
    .filter(heatPump => !technologyFilter[heatPump.marketingName])
    .forEach(heatPump => technologyFilter[heatPump.marketingName] = {
      marketingName: heatPump.marketingName,
      technologyKey: `step_heat_pumps.technology_${heatPump.normalizedMarketingName}`,
      disabled: false
    })

  for (const tf of Object.values(technologyFilter)) {
    tf.disabled = !filterByVoltage(activeVoltageFilter.value, filterByTechnology(unfilteredHeatPumpList.value, [tf])).length
  }

  return Object.values(technologyFilter)
})

function filterByVoltage(voltage: string, heatpumps: Heatpump[]): Heatpump[] {
  if (!voltage) {
    return heatpumps
  }

  hj('heat_pumps_filter_by_voltage')
  return heatpumps.filter((item: Heatpump) =>
    item.characteristics.find(
      char =>
        char.type == CharacteristicType.ELECTRICITY_CONSUMER &&
        (char as ECCharacteristic).inputVoltage.includes(voltage)
    )
  )
}

function filterByTechnology(heatPumpList: Heatpump[], technologyFilter: TechnologyFilter[]) {
  return heatPumpList.filter(hp => technologyFilter.map(tf => tf.marketingName).includes(hp.marketingName))
}

function filterByActiveTechnology(heatPumpList: Heatpump[]) {

  if (activeTechnologyFilter.value.length === 0) {
    return heatPumpList
  }

  hj('heat_pumps_filter_by_technology')

  return filterByTechnology(heatPumpList, technologyFilterOptions.value.filter(tf => activeTechnologyFilter.value.includes(tf.marketingName)))
}

function getFilteredHeatPumpList(value: string) {
  let filteredHeatPumps: Heatpump[] = unfilteredHeatPumpList.value

  if (value) {
    filteredHeatPumps = filterByVoltage(value, filteredHeatPumps)
  }

  return filterByActiveTechnology(filteredHeatPumps)
}

function filterHeatPumpList(): void {
  let filteredHeatPumps = getFilteredHeatPumpList(activeVoltageFilter.value)

  projectStore.products[ProductType.HEATPUMP] = filteredHeatPumps.length ? filteredHeatPumps : undefined
}

function isVoltageBtnDisabled(voltage: '230V' | '400V'): boolean {
  return getFilteredHeatPumpList(voltage)?.length === 0
}

function voltageFilterClicked(e: Event): void {
  const elem = e.target as HTMLInputElement
  activeVoltageFilter.value = activeVoltageFilter.value === elem.value ? '' : elem.value
  filterHeatPumpList()
}

</script>

<template>
  <div class="product__filter product__filter-bar card">
    <div class="technology-filter">
      <div
        v-for="technologyFilter in technologyFilterOptions"
        :key="technologyFilter"
        :class="{ 'is--disabled': technologyFilter.disabled}"
        class="technology-filter-item">
        <label>
          <input
            :id="technologyFilter.marketingName"
            v-model="activeTechnologyFilter"
            :disabled="technologyFilter.disabled"
            :value="technologyFilter.marketingName"
            type="checkbox"
            @change="filterHeatPumpList"
          >
          <span class="form__cm is--large" />
        </label>

        <div>
          <label :for="technologyFilter.marketingName" class="form__checkbox form-segment-toggle__checkbox">
            <b>{{ technologyFilter.marketingName }}</b>
          </label>

          <label
            v-if="$te(technologyFilter.technologyKey)" :for="technologyFilter.marketingName"
            class="form__checkbox form-segment-toggle__checkbox">
            {{ $t(technologyFilter.technologyKey) }}
          </label>
        </div>
      </div>
    </div>

    <div v-if="userStore.enableHeatpumpVoltageFilter" class="product__filter-buttons">
      <button
        :class="{ 'is--tertiary': activeVoltageFilter === '230V' }"
        :disabled="isVoltageBtnDisabled('230V')"
        class="btn has--outline is--secondary"
        tabindex="2"
        value="230V"
        @click="voltageFilterClicked"
      >
        230V
      </button>
      <button
        :class="{ 'is--tertiary': activeVoltageFilter === '400V' }"
        :disabled="isVoltageBtnDisabled('400V')"
        class="btn has--outline is--secondary"
        tabindex="3"
        value="400V"
        @click="voltageFilterClicked"
      >
        400V
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';

.product__filter {
  &-bar {
    display: flex;
    padding: 0.5rem;
    gap: 0.5rem;
    margin-bottom: 2rem;
    overflow: initial;
    border-radius: 0.25rem;

    @include media('>=desktop') {
      padding: 1rem;
      gap: 1rem;
    }

    @include media('<tablet') {
      flex-wrap: wrap;
    }

    label {
      display: flex;
      margin-bottom: 0;
      flex: 1;

      input {
        outline: 0;
        border: 0;
      }
    }
  }

  .technology-filter {
    display: flex;
    flex: 1;
    flex-wrap: wrap;
    gap: 1rem;
  }

  .technology-filter-item {
    display: flex;
    align-items: center;
    border-right: 1px lightgray solid;
    padding-right: 1rem;

    &:last-child {
      border-right: none;
    }

    &.is--disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }

  &-buttons {
    display: flex;
    gap: 0.5rem;
    padding-left: 0.5rem;
    align-items: center;
    justify-content: center;

    @include media('>=desktop') {
      gap: 1rem;
      padding-left: 1rem;
    }

    button {
      min-width: 0;
      align-items: center;
      padding-left: 1rem;
      padding-right: 1rem;

      @include themify($themes) {
        border-radius: rem-calc(themed('btnSubNavBorderRadius'));
      }
    }
  }

  .form__element {
    display: flex;
    flex-direction: column;
    margin-bottom: 0;
    margin-right: 0;
    max-width: 100%;
  }
}
</style>
